import { getVisitorId, getVisitId, consumeNewVisitFlag, getReferralCode } from './utils';
import { apiFetch } from './api';
import type { AnalyticsEvent } from '../types';

const eventQueue: AnalyticsEvent[] = [];
let flushTimer: number | null = null;

function detectDevice(): 'mobile' | 'desktop' {
  if (typeof navigator === 'undefined') return 'desktop';
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isCoarsePointer =
    typeof window !== 'undefined' && window.matchMedia != null && window.matchMedia('(pointer: coarse)').matches;
  return isMobileUA || isCoarsePointer ? 'mobile' : 'desktop';
}

/**
 * 入站來源快照：新到訪的第一刻補發，秒退訪客的入口數據也不會丟。
 * （?r= 擷取必須在 RefCapture 清 URL 前發生——本事件由到訪內第一個 trackEvent 觸發，
 *  而第一個事件在 initial load 時早於 URL 清理，故安全。）
 */
function buildSessionEntryEvent(visitorId: string, sessionId: string): AnalyticsEvent {
  let referrerDomain = 'direct';
  if (typeof document !== 'undefined' && document.referrer) {
    try {
      referrerDomain = new URL(document.referrer).hostname.replace(/^www\./, '');
    } catch {
      // 無法解析的 referrer 當 direct
    }
  }
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  return {
    eventType: 'session_entry',
    eventData: {
      landing: typeof window !== 'undefined' ? window.location.pathname : '',
      referrerDomain,
      utmSource: params?.get('utm_source') || null,
      utmMedium: params?.get('utm_medium') || null,
      utmCampaign: params?.get('utm_campaign') || null,
      referralCode: params?.get('r') || params?.get('ref') || params?.get('gs') || null,
      device: detectDevice(),
    },
    sessionId,
    visitorId,
    pagePath: typeof window !== 'undefined' ? window.location.pathname : '',
    timestamp: Math.floor(Date.now() / 1000),
  };
}

export function trackEvent(
  eventType: string,
  eventData: Record<string, unknown> = {}
) {
  // Attach active referral code so every event can be attributed to a referrer
  const referralCode = typeof window !== 'undefined' ? getReferralCode() : null;
  const enrichedEventData: Record<string, unknown> = { ...eventData };
  if (referralCode) {
    enrichedEventData.referralCode = referralCode;
  }

  const sessionId = getVisitId(); // 到訪級：可能在此刻建立新到訪
  const visitorId = getVisitorId(); // 訪客級：持久

  // 新到訪剛建立 → session_entry 排在本次到訪第一個事件之前
  const isNewVisit = consumeNewVisitFlag();
  if (isNewVisit) {
    eventQueue.push(buildSessionEntryEvent(visitorId, sessionId));
  }

  eventQueue.push({
    eventType,
    eventData: enrichedEventData,
    sessionId,
    visitorId,
    pagePath: typeof window !== 'undefined' ? window.location.pathname : '',
    timestamp: Math.floor(Date.now() / 1000),
  });

  if (isNewVisit) {
    // session_entry 必須立即落庫——不等 5 秒 batch，防止客人秒退丟失入口數據
    flushEvents();
    return;
  }
  if (!flushTimer) {
    flushTimer = window.setTimeout(flushEvents, 5000);
  }
  if (eventQueue.length >= 10) {
    flushEvents();
  }
}

export function flushEvents() {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (eventQueue.length === 0) return;
  const events = [...eventQueue];
  eventQueue.length = 0;

  apiFetch('/public/analytics/events', {
    method: 'POST',
    body: JSON.stringify({ events }),
    keepalive: true,
  } as RequestInit).catch(() => {
    // Silent failure: analytics should never block user flow
  });
}

export function trackPageView(page: string, referrer?: string) {
  trackEvent('page_view', { page, referrer: referrer || document.referrer });
}

export function trackProductView(productId: number, sku: string, name: string) {
  trackEvent('product_view', { productId, sku, name });
}

export function trackAddToCart(productId: number, quantity: number, unitPrice: number) {
  trackEvent('add_to_cart', { productId, quantity, unitPrice });
}

export function trackBeginCheckout(cartValue: number, itemCount: number) {
  trackEvent('begin_checkout', { cartValue, itemCount });
}

export function trackWhatsAppRedirect(target: 'order_confirm' | 'offer_claim', ref: string) {
  trackEvent('whatsapp_redirect', { target, orderNum: target === 'order_confirm' ? ref : undefined, offerKey: target === 'offer_claim' ? ref : undefined });
}

export function trackOrderCreated(orderNum: string, finalPrice: number) {
  let attribution: Record<string, unknown> | undefined;
  if (typeof window !== 'undefined') {
    try {
      const raw = window.sessionStorage.getItem('x1493_popup_attribution');
      if (raw) {
        const parsed = JSON.parse(raw) as { popupId?: string; popupName?: string; clickedAt?: number };
        if (parsed.popupId) {
          attribution = {
            attributionSource: 'popup',
            attributionPopupId: parsed.popupId,
            attributionPopupName: parsed.popupName,
            attributionClickedAt: parsed.clickedAt,
          };
        }
      }
    } catch {
      // Ignore sessionStorage parse errors
    }
  }
  trackEvent('order_created', { orderNum, finalPrice, ...attribution });
}

export function trackWhatsAppClick(orderNum: string, action: string) {
  trackEvent('whatsapp_click', { orderNum, action });
}

export function trackReferralLinkClick(code: string, landingPage: string) {
  trackEvent('referral_link_click', { code, landingPage });
}
